import { Box, Button, Flex, Icon, Table, Tbody, Td, Text, Tr } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FaDownload } from "react-icons/fa";
import { BseDto, BseStateNameDto } from "../../../../autogen/bff-api";
import { downloadBffFile, jsonPost } from "../../../../common/download/downloadBffFile";
import { generateHTMLFromTextDocument } from "../../../../common/input/TipTap/downloadTextDocument";
import { Explanation } from "../../../../common/support/Explanation/Explanation";
import { sanitizeFilename } from "../../../../common/utils/sanitizeUtils";

interface Props {
  data: BseDto;
}

export const downloadProtocol = ({ eventId, content, fileName }: { eventId: string; content?: string; fileName: string }) =>
  downloadBffFile(
    `/api/supplier/basic/events/${eventId}/public-procurement-protocol-part-one`,
    sanitizeFilename(`${fileName}.pdf`),
    jsonPost({ descriptionHtml: generateHTMLFromTextDocument(content ?? "") }),
  );

export const downloadQaReport = ({ eventId, fileName }: { eventId: string; fileName: string }) =>
  downloadBffFile(`/api/supplier/basic/events/${eventId}/qa-report`, sanitizeFilename(`${fileName}.pdf`));

export const BseReports = ({ data }: Props) => {
  const { t } = useTranslation();

  const getCannotDownloadExplanation = (): string | null => {
    if (data.stateName === BseStateNameDto.Closed || data.stateName === BseStateNameDto.Cancelled) return null;
    if (!data.awardedFields) return t("The event has to be awarded before the protocol can be downloaded");
    if (!data.awardedFields.eventValue) return t("You must provide a contract value before downloading this report");
    return null;
  };

  const cannotDownloadExplanation = getCannotDownloadExplanation();
  const canDownload = cannotDownloadExplanation === null;

  return (
    <Box borderRadius={10} mt="6">
      <Text fontWeight={"semibold"} pb="2">
        {t("Reports")}
      </Text>
      <Flex border="1px solid" borderColor="smBorder" rounded="lg">
        <Table variant="simple" size={"sm"}>
          <Tbody>
            <Tr>
              <Td>{t("Procurement protocol")}</Td>
              <Td>
                <Flex justifyContent="end">
                  <Explanation text={cannotDownloadExplanation ?? ""} enabled={!canDownload}>
                    <Button
                      size="sm"
                      leftIcon={<Icon as={FaDownload} w="15px" h="15px" />}
                      isDisabled={!canDownload}
                      variant={"ghost"}
                      colorScheme="blue"
                      onClick={() =>
                        downloadProtocol({
                          eventId: data.id,
                          content: data.awardedFields?.description,
                          fileName: `${data.title} - Procurement protocol`,
                        })
                      }
                    >
                      {t("Download")}
                    </Button>
                  </Explanation>
                </Flex>
              </Td>
            </Tr>
            <Tr>
              <Td>{t("Questions and announcements")}</Td>
              <Td>
                <Flex justifyContent="end">
                  <Button
                    size="sm"
                    leftIcon={<Icon as={FaDownload} />}
                    variant={"ghost"}
                    colorScheme="blue"
                    onClick={() =>
                      downloadQaReport({
                        eventId: data.id,
                        fileName: `${data.title} - Questions and announcements`,
                      })
                    }
                  >
                    {t("Download")}
                  </Button>
                </Flex>
              </Td>
            </Tr>
          </Tbody>
        </Table>
      </Flex>
    </Box>
  );
};
